import { FlagContext, useFlagReady } from '@ecs-na/access-flag-react-adapter';
import { useContext, useEffect, useState } from 'react';
import {
  getObservedBooleanAssignment,
  hasObservedAssignment,
  subscribeToAssignments
} from './eppo-assignment-store';

interface ResolvedBooleanFlag {
  /**
   * True only after the flag provider is ready and this specific flag has been
   * explicitly evaluated through the FeatureFlags facade.
   */
  ready: boolean;
  value: boolean;
}
interface UseResolvedBooleanFlagOptions {
  /**
   * Optional delay before marking this specific flag evaluation ready.
   *
   * This is useful for Datadog/OpenFeature routed flags where the first read
   * immediately after provider readiness can briefly return the default value
   * before the actual assignment propagates through the wrapper. During this
   * stabilization window, subscribe callbacks restart the timer and the final
   * value is re-read before ready=true is exposed.
   */
  stabilizeMs?: number;
  /**
   * Whether this hook should actively evaluate the flag.
   *
   * Defaults to true. Set to false after a page has intentionally opted out of a
   * late assignment, such as after a visual timeout fallback. This prevents a
   * later provider-ready notification from calling `flags.getBoolean(...)` and
   * emitting an assignment log for a variant the user did not actually see.
   */
  enabled?: boolean;
  /**
   * Publish readiness as soon as an actual Eppo assignment has been observed for
   * this flag and the current subject, instead of waiting a blind
   * `stabilizeMs` window.
   *
   * A boolean read alone cannot distinguish an assigned-control `false` from the
   * default fallback `false`, so for Eppo-backed experiments the assignment
   * event (see `eppo-assignment-store`) is the readiness signal. When no
   * assignment is observed the hook stays not-ready so the page-level timeout
   * can decide the fallback.
   */
  waitForAssignment?: boolean;
}

/**
 * Evaluates a boolean flag after provider readiness and exposes a readiness bit
 * for this specific evaluation.
 *
 * This is intentionally different from using `useBooleanFlag(key, false)`
 * directly for analytics attribution. The default `false` value is
 * indistinguishable from an assigned-control `false`, and `useFlagReady()` can
 * become true before the hook value has propagated through React. For OP/Decibel
 * stamping we need:
 *
 *   provider ready -> explicit flag evaluation -> campaign attribution applied
 *
 * rather than:
 *
 *   provider ready -> current hook value, possibly still default false
 */
export function useResolvedBooleanFlag(
  flagKey: string,
  defaultValue = false,
  { stabilizeMs = 0, enabled = true, waitForAssignment = false }: UseResolvedBooleanFlagOptions = {}
): ResolvedBooleanFlag {
  const flagContext = useContext(FlagContext);
  if (!flagContext) {
    throw new Error('useResolvedBooleanFlag must be used within FeatureFlagRoot');
  }

  const { flags } = flagContext;
  const flagReady = useFlagReady();
  const [state, setState] = useState<ResolvedBooleanFlag>({
    ready: false,
    value: defaultValue
  });

  useEffect(() => {
    let alive = true;
    let stabilizeTimer: number | null = null;

    const clearStabilizeTimer = () => {
      if (stabilizeTimer !== null) {
        window.clearTimeout(stabilizeTimer);
        stabilizeTimer = null;
      }
    };

    if (!enabled || !flagReady || !flags.isReady()) {
      clearStabilizeTimer();
      setState({
        ready: false,
        value: defaultValue
      });
      return;
    }

    if (waitForAssignment) {
      const evaluateAssignment = async () => {
        const evaluated = await Promise.resolve(flags.getBoolean(flagKey, defaultValue));

        if (!alive) {
          return;
        }

        // Prefer the evaluated value; fall back to the observed variation when
        // the evaluation could not produce a boolean.
        const observedValue = getObservedBooleanAssignment(flagKey);
        const value = typeof evaluated === 'boolean' ? evaluated : (observedValue ?? defaultValue);

        if (hasObservedAssignment(flagKey)) {
          setState({ ready: true, value });
          return;
        }

        // No assignment observed yet: expose the current read but stay
        // not-ready so the page-level timeout/fallback owns the decision.
        setState((current) => (current.ready ? current : { ready: false, value }));
      };

      // Covers both the synchronous assignment logged during getBoolean and the
      // session-cached assignment from a refresh/second page load.
      void evaluateAssignment();

      const unsubscribeAssignments = subscribeToAssignments((assignment) => {
        if (assignment.featureFlag === flagKey) {
          void evaluateAssignment();
        }
      });

      const unsubscribeFlags = flags.subscribe(() => {
        if (flags.isReady()) {
          void evaluateAssignment();
        }
      });

      return () => {
        alive = false;
        unsubscribeAssignments();
        unsubscribeFlags();
      };
    }

    const evaluate = async () => {
      const value = await Promise.resolve(flags.getBoolean(flagKey, defaultValue));

      if (alive) {
        setState((current) => ({
          ready: false,
          value: current.ready ? current.value : value
        }));
      }

      clearStabilizeTimer();

      const publish = async () => {
        const stabilizedValue = await Promise.resolve(flags.getBoolean(flagKey, defaultValue));

        if (alive) {
          setState({
            ready: true,
            value: stabilizedValue
          });
        }
      };

      if (stabilizeMs > 0) {
        stabilizeTimer = window.setTimeout(() => {
          stabilizeTimer = null;
          void publish();
        }, stabilizeMs);
      } else {
        void publish();
      }
    };

    void evaluate();

    const unsubscribe = flags.subscribe(() => {
      if (flags.isReady()) {
        // Restart the stabilization window whenever the provider publishes a
        // post-ready update. This prevents attribution from being stamped from a
        // transient default read immediately before the real assignment arrives.
        void evaluate();
      }
    });

    return () => {
      alive = false;
      clearStabilizeTimer();
      unsubscribe();
    };
  }, [flags, flagKey, defaultValue, flagReady, stabilizeMs, enabled, waitForAssignment]);

  return state;
}
